import { useScrollReveal } from '../hooks/useScrollReveal';

const stepGroups = [
  {
    tier: 'primary' as const,
    label: '前期准备',
    subtitle: '数据采集与预处理',
    steps: [
      { num: '01', label: '数字教研组分工', desc: '多智能体分配课堂结构、内容理解、互动识别等任务', icon: 'ri-team-line', tag: '智能体协同' },
      { num: '02', label: '课堂实录', desc: '视频、音频、ASR文本、课件、教材同时间轴对齐', icon: 'ri-record-circle-line', tag: '多源对齐' },
      { num: '03', label: '有效切片', desc: '从长视频中提取导入、案例、提问、互动、总结片段', icon: 'ri-scissors-cut-line', tag: '关键片段' },
    ],
  },
  {
    tier: 'accent' as const,
    label: '中期处理',
    subtitle: '智能分析与评价判断',
    steps: [
      { num: '04', label: '多模态识别', desc: '文字解释"讲了什么"，声音判断"怎样推进"，画面说明"发生了什么"', icon: 'ri-scan-2-line', tag: '三类互证' },
      { num: '05', label: '指标映射与专家样本学习', desc: '课堂片段映射到评价维度，结合专家标注形成专业判断', icon: 'ri-map-pin-2-line', tag: '专业标准' },
    ],
  },
  {
    tier: 'secondary' as const,
    label: '后期输出',
    subtitle: '证据生成与结果交付',
    steps: [
      { num: '06', label: '证据生成', desc: '评价结论连接课堂原话、时间戳、课件页和视频片段', icon: 'ri-links-line', tag: '可追溯' },
      { num: '07', label: '评价输出与群体分析', desc: '综合结果、维度诊断、三档分层、证据链、改进建议', icon: 'ri-file-chart-line', tag: '输出' },
    ],
  },
];

const tierColor = {
  primary: {
    num: 'text-primary-400',
    numBg: 'bg-primary-50',
    accent: 'text-primary-500',
    line: 'from-primary-200/60',
    bar: 'bg-primary-400',
    tag: 'text-primary-600',
  },
  accent: {
    num: 'text-accent-400',
    numBg: 'bg-accent-50',
    accent: 'text-accent-500',
    line: 'from-accent-200/60',
    bar: 'bg-accent-400',
    tag: 'text-accent-600',
  },
  secondary: {
    num: 'text-secondary-400',
    numBg: 'bg-secondary-50',
    accent: 'text-secondary-500',
    line: 'from-secondary-200/60',
    bar: 'bg-secondary-400',
    tag: 'text-secondary-600',
  },
};

// 扁平化所有步骤用于极简列表
const allSteps = stepGroups.flatMap((g) =>
  g.steps.map((s) => ({ ...s, groupLabel: g.label, tier: g.tier }))
);

export default function P24SevenStepsNew({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);

  return (
    <div className="relative min-h-screen flex flex-col overflow-x-hidden overflow-y-auto bg-background-50">
      {/* 极简背景：仅一个极淡的渐变光晕 */}
      <div className="absolute inset-0 bg-gradient-to-b from-background-50 via-background-50 to-background-100 pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-primary-50/20 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative z-10 flex flex-col h-full px-8 md:px-16 lg:px-24 max-w-7xl mx-auto w-full">
        {/* 标题区 —— 极度精简 */}
        <div ref={titleRef} className="shrink-0 text-center pt-16 md:pt-20 lg:pt-24 pb-4 md:pb-6">
          <span className={`inline-flex items-center gap-2 text-[11px] font-label font-medium text-foreground-400 uppercase tracking-[0.2em] mb-2 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}>
            <span className="w-1 h-1 rounded-full bg-accent-400" />
            AI评课的七步流程
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 tracking-tight transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            从课堂材料到<span className="text-primary-500">质量分析</span>
          </h2>
        </div>

        {/* 极简七步列表 —— 核心区域 */}
        <div className="flex-1 flex flex-col justify-center min-h-0 py-2 md:py-4">
          {allSteps.map((step, i) => {
            const c = tierColor[step.tier];
            const isFirstOfGroup = i === 0 || allSteps[i - 1].tier !== step.tier;
            const groupStartPad = isFirstOfGroup && i > 0 ? 'mt-4 md:mt-6' : '';
            return (
              <div
                key={step.num}
                className={`flex items-start gap-4 md:gap-6 lg:gap-8 ${groupStartPad} transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                style={{ transitionDelay: `${200 + i * 80}ms` }}
              >
                {/* 左边：超大数字 + 阶段指示 */}
                <div className="flex flex-col items-center flex-shrink-0 w-14 md:w-20 lg:w-24">
                  {/* 阶段小标签（仅每组第一步显示） */}
                  {isFirstOfGroup && (
                    <span className={`text-[9px] md:text-[10px] font-label font-semibold ${c.tag} uppercase tracking-wider mb-1`}>
                      {step.groupLabel}
                    </span>
                  )}
                  {!isFirstOfGroup && <div className="h-4 md:h-5" />}
                  <span className={`number-font text-3xl md:text-4xl lg:text-5xl ${c.num} leading-none select-none`}>
                    {step.num}
                  </span>
                </div>

                {/* 中间：内容区 */}
                <div className="flex-1 min-w-0 pt-1 md:pt-2">
                  <div className="flex items-center gap-2 md:gap-3 mb-1">
                    <h3 className="font-heading text-base md:text-lg lg:text-xl font-bold text-foreground-900">
                      {step.label}
                    </h3>
                    <span className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-label font-medium ${c.numBg} ${c.tag} border border-current/10`}>
                      {step.tag}
                    </span>
                  </div>
                  <p className="text-xs md:text-sm text-foreground-500 font-body leading-relaxed max-w-2xl">
                    {step.desc}
                  </p>
                </div>

                {/* 右边：极简图标 */}
                <div className="hidden md:flex items-center justify-center w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-background-100 border border-background-200/60 flex-shrink-0 mt-1">
                  <i className={`${step.icon} text-sm lg:text-base ${c.accent}`} />
                </div>
              </div>
            );
          })}

          {/* 底部分割线 */}
          <div className={`mt-4 md:mt-6 border-t border-background-200/50 transition-all duration-1000 ${titleVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '900ms' }} />
        </div>

        {/* 底部数据 —— 极度精简 */}
        <div className={`shrink-0 pb-4 md:pb-6 flex items-center justify-center gap-6 md:gap-10 transition-all duration-1000 delay-1000 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex items-center gap-2">
            <i className="ri-bar-chart-line text-xs text-foreground-400" />
            <span className="text-[11px] md:text-xs font-body text-foreground-500">
              全量572门课程验证 · 三档分层单调率 <span className="font-bold text-foreground-700">96%</span>
            </span>
          </div>
          <div className="w-px h-3 bg-foreground-200/30" />
          <div className="flex items-center gap-2">
            <i className="ri-information-line text-xs text-foreground-400" />
            <span className="text-[11px] md:text-xs font-body text-foreground-400">
              数值为三档单调率，非准确率
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}